'use client';

import { Button } from '@app/app/_components/Button/Button';
import { Heading } from '@app/app/_components/Heading/Heading';
import { CheckboxInput } from '@app/app/_components/Input/CheckboxInput';
import { Message } from '@app/app/_components/Message/Message';
import { ArrowRightIcon } from '@app/app/_icons/ArrowRightIcon';
import { useIsMobile } from '@app/infrastructure/application/useIsMobile';
import { FormStatus } from '@app/lib/form/contracts/FormStatus';
import { useLocale, useTranslations } from 'next-intl';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { useFormState } from 'react-dom';
import { useAppParameters } from '@app/app/_context/AppParametersContext/useAppParameters';
import { GlobalLoader } from '@app/app/_components/GlobalLoader/GlobalLoader';
import { EsalesTransportFooter } from '@app/app/esales/transport/components/EsalesTransportFooter';
import { FormState } from '@app/lib/form/contracts/FormState';
import { TextButton } from '@app/app/_components/TextButton/TextButton';
import { useHighContrastMode } from '@app/app/_context/AccessibilityContext/useHighContrastMode';
import {
    PersonalAccidentsInsuranceRequestResult,
    requestPersonalAccidentsInsurance,
} from '@app/service/eshop/personal-accidents/actions/requestPersonalAccidentsInsurance';
import { useMemo } from 'react';
import { InsuredPerson } from '@app/domain/model/eshop/personal-accidents/InsuredPerson';
import { GhostButton } from '@app/app/_components/GhostButton/GhostButton';
import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';
import { PlusIcon } from '@app/app/_icons/PlusIcon';
import { isViolationMessage } from '@app/lib/form/isViolationMessage';
import { PersonCard } from '@app/app/esales/personal-accidents/_sections/PersonalAccidentsLandingSection/PersonCard';
import { DiscountCodeData } from '@app/domain/model/eshop/transport/DiscountCodeData';
import { Input } from '@app/app/_components/Input/Input';
import { InformationPopover } from '@app/app/_components/InformationPopover/InformationPopover';
import { QuestionCircleIcon } from '@app/app/_icons/QuestionCircleIcon';
import { DiscountCode } from '@app/app/esales/transport/_sections/DiscountCode';
import { ValidationError } from 'yup';
import { buildViolations } from '@app/lib/form/buildViolations';
import { ViolationMap } from '@app/lib/form/contracts/ViolationMap';
import { RequestPersonalAccidentsInsurance } from '@app/service/eshop/personal-accidents/schemas/RequestPersonalAccidentsInsurance';
import { ensureString } from '@app/lib/util/ensureString';

interface Props {
    onSuccess?: (result: PersonalAccidentsInsuranceRequestResult) => void;
    backgroundImageSrc: string;
    mobileImageSrc: string;
    stakeholderLogoSrc: string;
    vigLogoSrc: string;
    insuredPersons: InsuredPerson[];
    handleAddPerson: () => void;
    handleRemovePerson: (id: string) => void;
    handleAgeChange: (id: string) => (e: React.ChangeEvent<HTMLInputElement>) => void;
    discountCodeData: DiscountCodeData;
    handleDiscountCode: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleApplyDiscountClick: () => Promise<void>;
    personalCode?: string;
}

export function PersonalAccidentsLandingSection({
    onSuccess,
    backgroundImageSrc,
    mobileImageSrc,
    stakeholderLogoSrc,
    vigLogoSrc,
    insuredPersons,
    handleAddPerson,
    handleRemovePerson,
    handleAgeChange,
    discountCodeData,
    handleDiscountCode,
    handleApplyDiscountClick,
    personalCode,
}: Props) {
    const t = useTranslations();
    const isMobile = useIsMobile(1024);
    const locale = useLocale();
    const parameters = useAppParameters();
    const isHighContrastMode = useHighContrastMode();
    const [isLoading, setIsLoading] = useState(false);
    const [clientValidationErrors, setClientValidationErrors] = useState<Nullable<ViolationMap>>(null);

    const [formState, formAction] = useFormState(
        (prevState: FormState<PersonalAccidentsInsuranceRequestResult>, formData: FormData) =>
            enhancedFormAction(prevState, formData),
        { status: FormStatus.Pending },
    );

    useEffect(() => {
        if (formState.status === FormStatus.Success && formState.data != null) {
            setIsLoading(false);
            setClientValidationErrors(null);
            onSuccess?.(formState.data);
        } else if (formState.status === FormStatus.Error) {
            setIsLoading(false);
        }
    }, [formState, onSuccess]);

    const enhancedFormAction = async (
        prevState: FormState<PersonalAccidentsInsuranceRequestResult>,
        formData: FormData,
    ) => {
        if (discountCodeData.validTravelCode) {
            formData.append('discountCode', discountCodeData.validTravelCode);
        }

        formData.append('insuredPersons', JSON.stringify(insuredPersons));

        return requestPersonalAccidentsInsurance(prevState, formData);
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setClientValidationErrors(null);

        const formData = new FormData(e.currentTarget);
        const personalCode = ensureString(formData.get('personalCode'), '');

        try {
            await RequestPersonalAccidentsInsurance.validate(
                {
                    personalCode: personalCode,
                    insuredPersons: insuredPersons,
                    discountCode: discountCodeData.validTravelCode,
                    acceptPrivacyPolicy: formData.get('acceptPrivacyPolicy') === 'true',
                },
                { abortEarly: false },
            );

            setIsLoading(true);
            formAction(formData);
        } catch (error) {
            if (error instanceof ValidationError) {
                const violations = buildViolations(error, 'general.validationError');
                setClientValidationErrors(violations.violations ?? null);
            }
        }
    };

    const personErrors = useMemo(() => {
        const violations = clientValidationErrors ?? formState.violations;
        const insuredPersonsViolations = violations?.insuredPersons;
        if (!insuredPersonsViolations || isViolationMessage(insuredPersonsViolations)) {
            return insuredPersons.map(() => undefined);
        }

        return insuredPersons.map((_, index) => {
            const personViolations = insuredPersonsViolations[index];
            if (!personViolations || isViolationMessage(personViolations)) {
                return undefined;
            }
            return personViolations.age;
        });
    }, [clientValidationErrors, formState.violations, insuredPersons]);

    return (
        <div className="flex min-h-screen flex-col">
            <GlobalLoader isVisible={isLoading} text={t('eshop.personalAccidents.loadingPremiums')} />
            <div className="relative flex flex-col bg-white py-12 lg:px-16">
                {!isMobile && !isHighContrastMode && (
                    <div className="absolute right-0 top-0 h-full max-h-[820px] lg:w-1/2 xl:w-[60%] 2xl:w-[90%]">
                        <Image
                            src={backgroundImageSrc}
                            draggable={false}
                            fill
                            className="object-cover object-center"
                            alt={t('eshop.transport.backgroundImageAlt')}
                        />
                    </div>
                )}
                <form onSubmit={handleSubmit} className="relative z-10 px-4 lg:max-w-[520px]">
                    <Heading className="mb-4 text-4xl 2xl:text-nowrap">
                        {t('eshop.personalAccidents.landingTitle')}
                    </Heading>

                    <div className="mb-10 flex flex-col gap-6">
                        {formState.status === FormStatus.Error && formState.violations == null && (
                            <Message>{t(formState.message)}</Message>
                        )}

                        <div className="flex w-full flex-col gap-6">
                            <Input
                                name="personalCode"
                                noSpaces
                                noSymbols
                                label={t('eshop.personalAccidents.personalCodeLabel')}
                                labelAddon={
                                    <InformationPopover content={t('eshop.personalAccidents.personalCodeTooltip')}>
                                        <QuestionCircleIcon className="size-4 text-neutral-500" />
                                    </InformationPopover>
                                }
                                placeholder={t('eshop.personalAccidents.personalCodePlaceholder')}
                                description={
                                    <InformationPopover
                                        content={t('eshop.personalAccidents.personalCompanyCodeDescriptionPopover')}
                                        className="underline">
                                        {t('eshop.travel.personalCompanyCodeDescriptionLink')}
                                    </InformationPopover>
                                }
                                error={clientValidationErrors?.personalCode ?? formState.violations?.personalCode}
                                defaultValue={personalCode}
                            />

                            <Paragraph className="-mb-2">{t('eshop.personalAccidents.maxFiveInsured')}</Paragraph>

                            <div className="flex flex-col gap-4">
                                {insuredPersons.map((person, index) => {
                                    return (
                                        <PersonCard
                                            key={person.id}
                                            person={person}
                                            index={index}
                                            handleAgeChange={handleAgeChange}
                                            handleRemovePerson={handleRemovePerson}
                                            ageError={personErrors[index]}
                                        />
                                    );
                                })}

                                {insuredPersons.length < 5 && (
                                    <GhostButton
                                        className="flex flex-row items-center gap-4 py-4"
                                        onClick={handleAddPerson}>
                                        <div className="flex flex-row items-center gap-2">
                                            <Heading level="h3" className="text-lg text-primary-500">
                                                {t('eshop.travel.addMorePersons')}
                                            </Heading>
                                            <PlusIcon className="size-6 text-primary-500" />
                                        </div>
                                        <Paragraph isGray size="small" className="self-center">
                                            {t.rich('eshop.travel.remainingOf', {
                                                currentAmount: insuredPersons.length,
                                                totalAmount: 5,
                                            })}
                                        </Paragraph>
                                    </GhostButton>
                                )}
                            </div>
                        </div>

                        <Message variant="info">
                            {t.rich('eshop.personalAccidents.ageExceedsMessage', {
                                link: chunks => (
                                    <TextButton
                                        target="_blank"
                                        href={
                                            locale === 'lt'
                                                ? parameters.eshopTravelInfoUrls.age70InfoLt
                                                : parameters.eshopTravelInfoUrls.age70InfoEn
                                        }
                                        preventDefault
                                        className="w-max p-0 underline underline-offset-2">
                                        {chunks}
                                    </TextButton>
                                ),
                            })}
                        </Message>

                        <CheckboxInput
                            name="acceptPrivacyPolicy"
                            label={t.rich('eshop.transport.acceptPrivacyPolicyLabel', {
                                link: chunks => (
                                    <Link
                                        className="underline"
                                        target="_blank"
                                        href={
                                            locale === 'lt'
                                                ? parameters.eshopInfoUrls.privacyPolicy
                                                : parameters.eshopInfoUrls.privacyPolicyEn
                                        }>
                                        {chunks}
                                    </Link>
                                ),
                            })}
                            error={
                                clientValidationErrors?.acceptPrivacyPolicy ?? formState.violations?.acceptPrivacyPolicy
                            }
                        />

                        <DiscountCode
                            discountCodeData={discountCodeData}
                            handleDiscountCode={handleDiscountCode}
                            handleApplyDiscountClick={handleApplyDiscountClick}
                        />
                    </div>

                    <div className="grid gap-6 md:grid-cols-2">
                        <Button rightAddon={<ArrowRightIcon className="size-4" />}>{t('general.next')}</Button>
                        <Button
                            href={parameters.eshopPersonalAccidentsInfoUrls.readMoreInfo}
                            target="_blank"
                            rightAddon={<ArrowRightIcon className="size-4" />}
                            variant="outlined">
                            {t('eshop.general.readMore')}
                        </Button>
                    </div>
                </form>
            </div>
            <div className="relative h-[600px] w-full lg:hidden">
                <Image
                    src={mobileImageSrc}
                    fill
                    className="object-cover"
                    alt={t('eshop.transport.backgroundImageAlt')}
                    unoptimized
                />
            </div>

            <EsalesTransportFooter
                className="lg:mt-[500px]"
                stakeholderLogoSrc={stakeholderLogoSrc}
                vigLogoSrc={vigLogoSrc}
            />
        </div>
    );
}
